import { Link } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import type { VendorDisplay } from '@/lib/vendor-display'
import type { VendorRef } from '@/lib/types'

/**
 * Phase 2 (Plan T11+T12) — accept either the canonical `VendorRef` shape
 * emitted by `GET /api/vulnerabilities/:id` (`affectedVendors`) and
 * `GET /api/vulnerabilities` (`topAffectedVendors`), or the resolved
 * fallback-chain `VendorDisplay[]` from `resolveVendorsForDisplay()`.
 *
 * `VendorRef` callers render as clickable filter links; Phase 2 also adds
 * a small "T1" pill for `tier === 1` (hyperscaler / identity vendors per
 * the canonical tier mapping). Fallback-chain entries render with their
 * existing provenance pill behaviour.
 */
interface AffectedVendorsBadgesProps {
  /**
   * Vendors to render. Accepts either:
   *   - `VendorRef[]` — canonical Phase 2 namespace shape. Renders as
   *     clickable filter links; Tier-1 vendors (`tier === 1`) get a "T1"
   *     pill.
   *   - `VendorDisplay[]` — resolved fallback chain output. Phase B entries
   *     render identically to `VendorRef[]` (including the Tier-1 pill);
   *     supply-chain / advisory entries render as plain-text badges with
   *     a small provenance pill so the user knows the canonical signal
   *     was empty.
   */
  vendors: VendorRef[] | VendorDisplay[]
}

/**
 * Type guard distinguishing the two accepted shapes. `VendorDisplay` is
 * tagged with a `source` field; `VendorRef` is not.
 */
function isVendorDisplayArray(
  vendors: VendorRef[] | VendorDisplay[],
): vendors is VendorDisplay[] {
  return vendors.length === 0 || 'source' in vendors[0]
}

/**
 * Small uppercase pill rendered next to fallback-tier vendor names so the
 * user can see "this didn't come from the canonical Phase B/C signal — it
 * was inferred from supply-chain incidents / vendor advisories on the same
 * page". Phase B vendors render exactly as they did before this change.
 */
function ProvenancePill({ source }: { source: VendorDisplay['source'] }) {
  if (source === 'phase-b') return null
  const label =
    source === 'supply-chain'
      ? 'from supply-chain'
      : source === 'advisory'
        ? 'from advisory'
        : // P2 tier 4 (audit item 11) — text-extraction is heuristic; the
          // UI label flags it so reviewers know this didn't come from a
          // typed signal.
          'from text'
  return (
    <span
      className="ml-1 text-[10px] uppercase tracking-wide text-muted-foreground"
      data-testid={`vendor-provenance-${source}`}
    >
      {label}
    </span>
  )
}

/**
 * Vendor badges for the Vulnerability Detail surface.
 *
 * Empty input renders nothing (per spec §6.3 — no zero-state stub). For
 * Phase B vendors (carrying a `vendorId`) each badge is a clickable Link to
 * `/vulnerabilities?vendor=<id>` — the vuln list page reads that param and
 * applies the server-side filter. Fallback-tier vendors (no `vendorId`)
 * render as plain text with a provenance pill.
 */
/**
 * Tier-1 pill rendered next to vendors that map to `vendor_kind` in the
 * canonical Tier-1 set (currently `hyperscaler` and `identity`). Surfaces
 * the elevated criticality of platform-vendor advisories. Phase 2 (T11+T12).
 */
function Tier1Pill() {
  return (
    <span
      className="ml-1 rounded-sm bg-primary/15 px-1 text-[10px] font-semibold uppercase tracking-wide text-primary"
      data-testid="vendor-tier-1-pill"
      aria-label="Tier 1 vendor"
    >
      T1
    </span>
  )
}

export function AffectedVendorsBadges({
  vendors,
}: AffectedVendorsBadgesProps) {
  if (vendors.length === 0) return null

  // Normalise to VendorDisplay[] so the render path is uniform. Canonical
  // `VendorRef[]` callers map to phase-b entries with no provenance pill;
  // tier is preserved so Tier-1 vendors render the "T1" pill.
  const displayVendors: VendorDisplay[] = isVendorDisplayArray(vendors)
    ? vendors
    : (vendors as VendorRef[]).map((v) => ({
        name: v.name,
        vendorId: v.id,
        source: 'phase-b',
        tier: v.tier,
      }))

  return (
    <div className="flex flex-wrap gap-2" data-testid="affected-vendors-badges">
      {displayVendors.map((v, i) => {
        const key = v.vendorId ?? `${v.source}-${v.name}-${i}`
        const isTier1 = v.source === 'phase-b' && v.tier === 1
        if (v.vendorId) {
          // Canonical (Phase B) — clickable badge linking to the vendor filter.
          return (
            <Link
              key={key}
              to={`/vulnerabilities?vendor=${v.vendorId}`}
              // R28: VENDOR NAME in an attribute — maskTextSelector covers the
              // badge's text node but not this.
              data-ph-no-capture
              aria-label={`Filter vulnerabilities by vendor ${v.name}`}
            >
              <Badge
                variant="secondary"
                className="cursor-pointer hover:bg-secondary/80"
              >
                {v.name}
                {isTier1 && <Tier1Pill />}
                <ProvenancePill source={v.source} />
              </Badge>
            </Link>
          )
        }
        // Fallback tier — plain-text badge with provenance pill. No filter
        // link because there is no canonical vendor UUID to filter on.
        return (
          <Badge
            key={key}
            variant="secondary"
            data-testid={`vendor-badge-${v.source}`}
          >
            {v.name}
            <ProvenancePill source={v.source} />
          </Badge>
        )
      })}
    </div>
  )
}
